<svelte:options customElement="hb-table" />

<script lang="ts">
  /**
   * Svelte Table WebComponent
   * =====================
   *
   * @contributors: Dario Caruso <dev@dariocaruso.info> (https://dariocaruso.info)
   *
   * @license: MIT License
   *
   */

  import dayjs from "dayjs";
  import "dayjs/locale/it";
  import { onMount } from "svelte";

  import type {
    IActionButton,
    IFilter,
    IRow,
    ITableHeader,
    Component,
  } from "./types/webcomponent.type";

  import type {
    Component as DialogComponent,
    Events as DialogEvents,
  } from "../dialog/types/webcomponent.type";
  import type {
    Component as DialogformComponent,
    Events as DialogformEvents,
  } from "../dialogform/types/webcomponent.type";

  import type { Events as PaginateEvents } from "../paginate/types/webcomponent.type";

  import pkg from "../../../package.json";

  import { addComponent, LanguageTranslator } from "wc-js-utils";
  import { dictionary } from "./libs/i18n";
  // import dispatch from "@app/functions/webcomponent";

  let {
    id = "",
    // style = "",

    externalfilter = undefined,

    rows = [],
    size = 12,
    page = 0,
    pages = 0,
    headers = [],
    actions = undefined,
    selectactions = undefined,
    selectrow = "",
    enableselect = undefined,
    disablepagination = false,
    add_item = undefined,
    total,
    i18nlang,
    selected,
    page_size_type,
    page_size_options,
    sort_default,
    sort_default_label,
    disable_paginate_sort,
    sort_strict_direction,
    sort_direction,
    is_loading,
    fixed_columns,
  }: Component = $props();

  let translator: LanguageTranslator = $state(
    new LanguageTranslator({ dictionary, lang: i18nlang }),
  );

  let searchOnRangeIsPresent = $state(false);

  let filters: IFilter[] = $state([]);
  let originalRows: IRow[];
  let selectedItems: string[] = $state([]);
  let sortedBy: string | undefined = $state(sort_default || undefined);
  let sortedDirection: "asc" | "desc" | "default" = $state(
    (sort_direction === "desc"
      ? "desc"
      : sort_direction === "default"
        ? "default"
        : "asc") as "asc" | "desc" | "default",
  );
  let modalConfirm: DialogComponent & { itemId: string; action: string } =
    $state({
      show: "no",
      itemId: "",
      action: "",
      confirmlabel: undefined,
      title: undefined,
      content: undefined,
      closelabel: undefined,
    });
  let modalConfirmForm: DialogComponent & {
    itemId: string;
    action: string;
    schema?: any;
  } = $state({
    show: "no",
    itemId: "",
    action: "",
    confirmlabel: undefined,
    title: undefined,
    content: undefined,
    closelabel: undefined,
  });

  let sortFields = $derived.by(() => {
    if (!headers || !Array.isArray(headers)) return [];
    return headers
      .filter((h) => h.type !== "actions" && !h.nosort)
      .map((h) => ({ value: h.key, label: h.label }));
  });

  let sortStrictDirectionTable: boolean = $derived(
    typeof sort_strict_direction === "string"
      ? sort_strict_direction === "true" || sort_strict_direction === "yes"
      : Boolean(sort_strict_direction),
  );

  let disablepaginationTable: boolean = $derived(
    typeof disablepagination === "string"
      ? disablepagination === "true" || disablepagination === "yes"
        ? true
        : false
      : disablepagination,
  );

  /** Server-driven filter/sort mode: only explicit `yes` / `true` (string or legacy boolean from Svelte parents). */
  let externalfilterTable: boolean = $derived(
    typeof externalfilter === "string"
      ? externalfilter === "true" || externalfilter === "yes"
      : Boolean(externalfilter),
  );

  let fixedColumnsTable = $derived(
    typeof fixed_columns === "string"
      ? fixed_columns === "true" || fixed_columns === "yes"
      : Boolean(fixed_columns),
  );

  /** Same coercion as `disablepagination`: attribute strings `"yes"` / `"true"` or boolean. */
  let isLoadingTable = $derived(
    typeof is_loading === "string"
      ? is_loading === "true" || is_loading === "yes"
      : Boolean(is_loading),
  );

  /** String attrs for `hb-paginate` (custom element); avoids `page ? x : "0"` treating `0` as falsy and keeps sync while loading overlay is shown. */
  function coerceNonNegIntString(v: unknown): string {
    if (v === undefined || v === null || v === "") return "0";
    const n = typeof v === "string" ? parseInt(v, 10) : Number(v);
    if (!Number.isFinite(n) || n < 0) return "0";
    return String(Math.floor(n));
  }

  /** Page count for client-side slice (`rowsTable`); `hb-paginate` uses `0` when there are no rows. */
  function computeLocalPagesCount(
    rowCount: number,
    pageSize: unknown,
  ): number {
    const sz =
      typeof pageSize === "string" ? parseInt(pageSize, 10) : Number(pageSize);
    if (!Number.isFinite(sz) || sz < 1) return rowCount > 0 ? 1 : 0;
    if (rowCount <= 0) return 0;
    return (
      Math.floor(rowCount / sz) + (rowCount % sz !== 0 ? 1 : 0)
    );
  }

  let paginatePageAttr = $derived(coerceNonNegIntString(page));

  /** Placeholder row count matches pagination `size` while loading (capped at 100). */
  let loadingSkeletonRowCount = $derived.by(() => {
    if (!isLoadingTable) return 0;
    const n = Number(size);
    if (!Number.isFinite(n) || n < 1) return 5;
    return Math.min(Math.floor(n), 100);
  });

  /** Resolve `headers` keys that use dot notation (same idea as `getObjVal`). */
  function getRowValueForSort(row: IRow, key: string): unknown {
    if (!key) return undefined;
    if (!key.includes(".")) return row[key];
    let val: unknown = row;
    for (const part of key.split(".")) {
      if (val == null || typeof val !== "object") return undefined;
      val = (val as Record<string, unknown>)[part];
    }
    return val;
  }

  /** Parsed IPv4 or CIDR (`/p` optional, 0–32). Invalid → `null`. */
  function parseIpOrCidr(raw: unknown): { addr: number; prefix: number } | null {
    if (raw == null || raw === "") return null;
    const t = String(raw).trim();
    const m = t.match(
      /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})(?:\/(\d{1,2}))?$/,
    );
    if (!m) return null;
    const oct = [
      parseInt(m[1], 10),
      parseInt(m[2], 10),
      parseInt(m[3], 10),
      parseInt(m[4], 10),
    ];
    if (oct.some((n) => !Number.isFinite(n) || n < 0 || n > 255)) return null;
    const addr =
      ((oct[0] << 24) | (oct[1] << 16) | (oct[2] << 8) | oct[3]) >>> 0;
    let prefix = 32;
    if (m[5] !== undefined) {
      const p = parseInt(m[5], 10);
      if (!Number.isFinite(p) || p < 0 || p > 32) return null;
      prefix = p;
    }
    return { addr, prefix };
  }

  function compareIpLike(
    va: unknown,
    vb: unknown,
    direction: "asc" | "desc",
  ): number | null {
    const pa = parseIpOrCidr(va);
    const pb = parseIpOrCidr(vb);
    if (pa && pb) {
      if (pa.addr !== pb.addr) {
        const cmp = pa.addr < pb.addr ? -1 : 1;
        return direction === "asc" ? cmp : -cmp;
      }
      if (pa.prefix !== pb.prefix) {
        const cmp = pa.prefix - pb.prefix;
        return direction === "asc" ? cmp : -cmp;
      }
      return 0;
    }
    if (pa && !pb) return direction === "asc" ? -1 : 1;
    if (!pa && pb) return direction === "asc" ? 1 : -1;
    return null;
  }

  function headerTypeForSortKey(key: string): string | undefined {
    return headers.find((h) => h.key === key)?.type;
  }

  /** Local (non-`externalfilter`) column sort: stable semantics, no in-place mutation of `rows`. */
  function compareLocalSort(
    a: IRow,
    b: IRow,
    key: string,
    direction: "asc" | "desc",
  ): number {
    const va = getRowValueForSort(a, key);
    const vb = getRowValueForSort(b, key);

    if (va === vb) return 0;

    const aEmpty = va == null || va === "";
    const bEmpty = vb == null || vb === "";
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return direction === "asc" ? 1 : -1;
    if (bEmpty) return direction === "asc" ? -1 : 1;

    if (typeof va === "number" && typeof vb === "number") {
      return direction === "asc" ? va - vb : vb - va;
    }

    const colType = headerTypeForSortKey(key);
    if (colType === "ip") {
      const ipCmp = compareIpLike(va, vb, direction);
      if (ipCmp !== null) return ipCmp;
    }

    const sa = String(va).toUpperCase();
    const sb = String(vb).toUpperCase();
    if (sa < sb) return direction === "asc" ? -1 : 1;
    if (sa > sb) return direction === "asc" ? 1 : -1;
    return 0;
  }

  let rowsTable = $derived.by(() => {
    let sortedRows = rows && rows.length && rows?.[0]?._id ? rows.slice() : [];
    if (filters?.length && !externalfilterTable) {
      for (const filter of filters) {
        if (filter.type === "datetime") {
          if (filter.start) {
            sortedRows = sortedRows.filter(
              (f) =>
                dayjs(getObjVal(f, filter)).valueOf() >=
                dayjs(filter.start).valueOf(),
            );
          }
          if (filter.end) {
            sortedRows = sortedRows.filter(
              (f) =>
                dayjs(getObjVal(f, filter)).valueOf() <=
                dayjs(filter.end).valueOf(),
            );
          }
        } else {
          sortedRows = sortedRows.filter((f) =>
            getObjVal(f, filter)
              .toString()
              .includes(filter.value || ""),
          );
        }
      }
    }

    if (sortedBy && !externalfilterTable) {
      if (sortedDirection === "asc" || sortedDirection === "desc") {
        const sortKey = sortedBy;
        const dir = sortedDirection;
        sortedRows = sortedRows
          .slice()
          .sort((a, b) => compareLocalSort(a, b, sortKey, dir));
      }
    }
    return sortedRows;
  });

  let paginatedInfo = $derived.by(() => {
    const result: Record<string, any> = {};
    if (!externalfilterTable) {
      result.total = rowsTable.length;
    } else if (total !== undefined && total !== null) {
      const t = Number(total);
      if (Number.isFinite(t)) result.total = t;
    }
    result.size = Number(size);
    if (page_size_type) result.page_size_type = page_size_type;
    if (page_size_options) result.page_size_options = page_size_options;
    if (sortFields.length && !disable_paginate_sort) {
      result.sort_fields = sortFields;
      result.sort_by = sortedBy || "";
      result.sort_direction = sortedBy
        ? sortedDirection
        : sortStrictDirectionTable
          ? "asc"
          : "default";
      if (sort_default) result.sort_default_value = sort_default;
      if (sort_default_label) result.sort_default_label = sort_default_label;
      if (sortStrictDirectionTable) result.sort_strict_direction = true;
    }
    return result;
  });

  let paginatePagesAttr = $derived(
    externalfilterTable
      ? coerceNonNegIntString(pages)
      : coerceNonNegIntString(
          computeLocalPagesCount(rowsTable.length, size),
        ),
  );

  $effect(() => {
    // if (style) {
    //     paginateStyleSetup?.vars,
    //   );
    //     dialogStyleSetup?.vars,
    //   );
    //     dialogformStyleSetup?.vars,
    //   );
    // }
    if (!add_item) add_item = false;
    if (typeof add_item === "string") {
      if (add_item === "true" || add_item === "yes") {
        add_item = true;
      } else {
        add_item = false;
      }
    }
    if (!modalConfirm) {
      modalConfirm = {
        show: "no",
        itemId: "",
        action: "",
        confirmlabel: undefined,
        title: undefined,
        content: undefined,
        closelabel: undefined,
      };
    }
    if (!modalConfirmForm) {
      modalConfirmForm = {
        show: "no",
        itemId: "",
        action: "",
        confirmlabel: undefined,
        title: undefined,
        content: undefined,
        closelabel: undefined,
      };
    }

    if (!selectactions) {
      selectactions = [];
    } else if (typeof selectactions === "string") {
      selectactions = JSON.parse(selectactions);
    }

    try {
      if (headers) {
        if (typeof headers === "string") headers = JSON.parse(headers);
        if (headers.find((f) => f.type === "datetime" && f.search))
          searchOnRangeIsPresent = true;
        headers.forEach((m) => {
          if (!m.sortBy) m.sortBy = "none";
          if (!m.type) m.type = "string";
          const mh = m as unknown as Record<string, unknown>;
          for (const key of ["centerCell", "centerHeader"] as const) {
            if (key in mh && typeof mh[key] === "string") {
              const c = mh[key] as string;
              mh[key] = c === "true" || c === "yes" ? true : false;
            }
          }
        });
      }

      if (actions) {
        if (typeof actions === "string") actions = JSON.parse(actions);
      } else {
        actions = undefined;
      }
      if (typeof rows === "string") {
        rows = Object.assign([], JSON.parse(rows));

        if (
          rowsTable.length &&
          rowsTable.length > Number(size) &&
          (!externalfilterTable || !pages)
        ) {
          pages =
            Math.floor(rowsTable.length / Number(size)) +
            (rowsTable.length % Number(size) ? 1 : 0);
        }
      }
      // const videos = component.getElementsByTagName("video");
      // for (let video of videos) {
      // 	video.load();
      // }
    } catch (err) {}
    // sortByKeyToggle = (key: string) => {
    // 	console.log(key);
    // 	const h = headers.find((f) => f.key === key);
    // 	console.log(h);

    // 	if (!h.sortBy || h.sortBy === "none") {
    // 		h.sortBy = "asc";
    // 	} else if (h.sortBy === "asc") {
    // 		h.sortBy = "desc";
    // 	} else if (h.sortBy === "desc") {
    // 		h.sortBy = null;
    // 	}
    // 	// rows = rows.sort((a, b) => {
    // 	// 	return b[key] - a[key];
    // 	// });
    // };
    // console.log("end computed");
    // console.log(size, page, pages, rows.length, initialDate, lastDate);

    // will only get called when the `color` changed.
    if (
      translator &&
      i18nlang &&
      translator.lang &&
      translator.lang !== i18nlang
    ) {
      translator = new LanguageTranslator({ dictionary, lang: i18nlang });
    }
  });

  function dispatch(name: string, detail: any) {
    $host().dispatchEvent(new CustomEvent(name, { detail }));
  }

  /** After local filters/sort shrink the list, keep `page` and `hb-paginate` in range. */
  $effect(() => {
    if (externalfilterTable) return;
    const pc = computeLocalPagesCount(rowsTable.length, size);
    const maxPage = Math.max(0, pc - 1);
    if (page > maxPage) {
      page = maxPage;
      dispatch("pageChange", { page: maxPage, pages: pc });
    }
  });

  function changePage(el: { detail: PaginateEvents["pageChange"] }) {
    // Never bail when `is_loading`: the parent may set loading in the same turn as
    // `pageChange`; skipping here leaves `page` stale and remounted `hb-paginate` shows page 1.
    if (!externalfilterTable) page = el.detail.page;
    selectedItems.length = 0;

    dispatch("pageChange", {
      page: el.detail.page,
      pages: el.detail.pages,
    });
  }

  function changePageSize(el: { detail: PaginateEvents["changePageSize"] }) {
    const newSize = el.detail.page_size;
    size = newSize;
    page = 0;
    selectedItems.length = 0;

    if (!externalfilterTable && rowsTable.length) {
      pages =
        Math.floor(rowsTable.length / Number(size)) +
        (rowsTable.length % Number(size) ? 1 : 0);
    }

    dispatch("changePageSize", {
      page_size: newSize,
    });
  }

  function changeSortFromPaginate(el: {
    detail: PaginateEvents["changeSort"];
  }) {
    const { sort_by, sort_direction } = el.detail;
    // Only clear the sorted column when the user chose the empty / default field.
    // `sort_direction === "default"` with a non-empty `sort_by` is valid from hb-paginate
    // (direction toggle); treating it as "clear sort" caused spurious changeSort and page resets.
    if (!sort_by) {
      sortedBy = undefined;
      sortedDirection = "asc";
    } else {
      sortedBy = sort_by;
      if (sort_direction === "desc") {
        sortedDirection = "desc";
      } else if (sort_direction === "asc") {
        sortedDirection = "asc";
      } else {
        sortedDirection = "default";
      }
    }
    dispatch("changeSort", {
      sortedBy,
      sortedDirection,
    });
  }

  // async function getHelloWorld() {
  // 	const { app } = await webcomponent({ text: "hello-world" });

  // 	return app();
  // }

  function headerCenterHeaderClass(
    h: Pick<ITableHeader, "centerHeader">,
  ): string {
    return h.centerHeader === true ? "hb-table-col--center-header" : "";
  }

  function headerCenterCellClass(h: Pick<ITableHeader, "centerCell">): string {
    return h.centerCell === true ? "hb-table-col--center-cell" : "";
  }

  function getObjVal(
    obj: IRow,
    opts: { key: string; type?: string; format?: string; truncateAt?: number },
    trim?: boolean,
  ): string | number {
    if (!opts) {
      return "";
    }
    let value;
    if (!opts.key.includes(".")) {
      value = obj[opts.key];
    } else {
      let val: any = obj;

      for (const k of opts.key.split(".")) {
        if (!val[k]) {
          value = undefined;
          break;
        }
        val = val[k];
        value = val;
      }
    }

    if (!value && value !== 0) {
      return "";
    } else if (!opts.type || opts.type === "string" || opts.type === "enum") {
      if (trim && opts.truncateAt && value.length > opts.truncateAt) {
        return value.substring(0, opts.truncateAt) + "...";
      } else {
        return value;
      }
    } else if (opts.type === "ip") {
      const s = String(value).trim();
      if (trim && opts.truncateAt && s.length > opts.truncateAt) {
        return s.substring(0, opts.truncateAt) + "...";
      }
      return s;
    } else if (opts.type === "number") {
      return Number(value);
    } else if (opts.type === "datetime") {
      return opts.format
        ? dayjs(value).format(opts.format)
        : dayjs(value).format();
    } else {
      return "";
    }
  }

  function removeFilter(key: string) {
    dispatch("removeFilter", {
      key,
    });
    if (filters.find((f) => f.key === key)) {
      filters = filters.filter((f) => f.key !== key);
    }

    selectedItems.length = 0;
  }
  function setFilter(filter: IFilter) {
    dispatch("changeFilter", {
      filter,
    });
    const filterExist = filters.find((f) => f.key === filter.key);

    if (filterExist) {
      filterExist.key = filter.key;
      filterExist.type = filter.type;
      filterExist.value = filter.value;
      filterExist.start = filter.start;
      filterExist.end = filter.end;

      filters = filters;
    } else {
      filters.push({
        key: filter.key,
        type: filter.type,
        value: filter.value,
        start: filter.start,
        end: filter.end,
      });
      filters = filters;
    }

    selectedItems.length = 0;
  }

  function searchInput(target: any, h: ITableHeader) {
    const value = target.value;
    if (h.type !== "actions" && value && value.length) {
      setFilter({
        key: h.key,
        type: h.type,
        value,
      });
    } else {
      removeFilter(h.key);
    }
  }
  function changeStartDate(target: any, th: ITableHeader) {
    const newDate = target.value;
    const filterExists = filters.find((f) => f.key === th.key);
    setFilter({
      key: th.key,
      type: th.type as any,
      start: dayjs(newDate, "YYYY-MM-DD").toDate(),
      end: filterExists && filterExists.end ? filterExists.end : undefined,
    });
  }
  function changeEndDate(target: any, th: ITableHeader) {
    const newDate = target.value;
    const filterExists = filters.find((f) => f.key === th.key);
    setFilter({
      key: th.key,
      type: th.type as any,
      end: dayjs(newDate, "YYYY-MM-DD").toDate(),
      start:
        filterExists && filterExists.start ? filterExists.start : undefined,
    });
  }

  function handleClickOnCustomAction(item: IRow, action: IActionButton) {
    console.log("action", action, item);

    dispatch("tableCustomActionClick", {
      itemId: item._id,
      action: action.name,
    });
    console.log("tttttttt", action, modalConfirm);
    if (action.confirm) {
      modalConfirm = {
        show: "yes",
        itemId: item._id,
        action: action.name,
        confirmlabel: action.confirm.confirmLabel,
        title: action.confirm.title,
        content: action.confirm.content,
      };
      // show modal
    } else if (action.edit) {
      modalConfirmForm = {
        show: "yes",
        itemId: item._id,
        action: action.name,
        confirmlabel: action.edit.confirmLabel,
        title: action.edit.title,
        schema: action.edit.schema,
      };
    }
  }
  function handleClickOnAction(itemId: string, action: string) {
    console.log("handleClickOnAction", itemId, action);
    const actionButton = actions?.find?.((f) => f?.name === action);
    if (!actionButton)
      return console.error(`action button not found: ${action}`);
    if (actionButton?.confirm) {
      modalConfirm = {
        show: "yes",
        itemId,
        action,
        confirmlabel: actionButton.confirm.confirmLabel,
      };
    } else if (actionButton?.edit) {
      modalConfirmForm = {
        show: "yes",
        itemId,
        action,
        confirmlabel: actionButton.edit.confirmLabel,
        title: actionButton.edit.title,
        schema: actionButton.edit.schema,
      };
    } else {
      dispatch("tableaction", {
        itemId,
        action,
      });
    }
  }
  function handleClickOnRow(itemId: string, colId: string) {
    console.log("cellclick", itemId, colId);
    dispatch("cellclick", {
      rowId: itemId,
      colId: colId,
    });
  }

  function handleSelectedItem(itemId: string, target: any) {
    console.log(itemId, target.checked, selectedItems);
    if (!target || !itemId) return console.error(`wrong params`);
    if (target.checked && !selectedItems.find((f) => f === itemId))
      selectedItems.push(itemId);
    if (!target.checked && selectedItems.find((f) => f === itemId))
      selectedItems.splice(
        selectedItems.findIndex((f) => f === itemId),
        1,
      );
    console.log(itemId, target.checked, selectedItems);
    selectedItems = selectedItems;
  }

  function handleEnableSelector() {
    if (enableselect) {
      enableselect = undefined;
      selectedItems.length = 0;
    } else {
      enableselect = "yes";
    }
  }

  function handleClickOnMultipleSelectAction(buttonName: string) {
    console.log("action", buttonName, buttonName);
    dispatch("actiononselected", {
      key: buttonName,
      selectedItems,
    });
  }
  function selectAllElements() {
    selectedItems = rowsTable.map((m) => m._id);
  }
  function deSelectAllElements() {
    selectedItems.length = 0;
  }
  function clickonrow(itemId: string) {
    dispatch("clickonrow", {
      itemId,
    });
  }

  addComponent({
    repoName: "@htmlbricks/hb-paginate",
    version: pkg.version,
  });
  addComponent({
    repoName: "@htmlbricks/hb-dialog",
    version: pkg.version,
  });
  addComponent({
    repoName: "@htmlbricks/hb-dialogform",
    version: pkg.version,
  });
  addComponent({
    repoName: "@htmlbricks/hb-tooltip",
    version: pkg.version,
  });

  /**
   * Reads the **resolved** Bulma theme on this host (shadow theme stack), not only
   * `prefers-color-scheme` — avoids outlined actions when the OS is dark but the UI
   * forces a light Bulma scheme on `:host` (or the inverse).
   */
  function brightnessFromBulmaHost(): boolean | null {
    try {
      const host = $host() as HTMLElement | undefined;
      if (!host || typeof getComputedStyle === "undefined") return null;
      const cs = getComputedStyle(host);
      for (const prop of [
        "--bulma-scheme-brightness",
        "--bulma-hb-def-scheme-brightness",
      ] as const) {
        const raw = cs.getPropertyValue(prop).trim();
        if (!raw) continue;
        const v = raw.replace(/^["']+|["']+$/g, "").toLowerCase();
        if (v === "dark") return true;
        if (v === "light") return false;
      }
      const lRaw = cs.getPropertyValue("--bulma-scheme-main-l").trim();
      const l = parseFloat(lRaw);
      /* Bulma light uses ~100%, dark ~9%; mid values are ambiguous — do not guess “dark”. */
      if (Number.isFinite(l)) {
        if (l <= 22) return true;
        if (l >= 70) return false;
      }
    } catch {
      /* ignore */
    }
    return null;
  }

  /**
   * Matches Bulma hb-wc theme hooks: `data-theme` / `.theme-*` on the host, `html`, `body`.
   */
  function documentExplicitColorScheme(): "dark" | "light" | null {
    if (typeof window === "undefined") return null;
    for (const el of [
      $host() as Element | undefined,
      document.documentElement,
      document.body,
    ]) {
      if (!el) continue;
      const dt = el.getAttribute("data-theme");
      if (dt === "dark") return "dark";
      if (dt === "light") return "light";
      if (el.classList.contains("theme-dark")) return "dark";
      if (el.classList.contains("theme-light")) return "light";
    }
    return null;
  }

  function computeColorSchemeDark(): boolean {
    const fromBulma = brightnessFromBulmaHost();
    if (fromBulma !== null) return fromBulma;
    const explicit = documentExplicitColorScheme();
    if (explicit === "dark") return true;
    if (explicit === "light") return false;
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  }

  let colorSchemeDark = $state(
    typeof window !== "undefined" ? computeColorSchemeDark() : false,
  );

  function onColorSchemeSignal() {
    const next = computeColorSchemeDark();
    if (next === colorSchemeDark) return;
    colorSchemeDark = next;
  }

  onMount(() => {
    const syncScheme = () => {
      colorSchemeDark = computeColorSchemeDark();
    };
    syncScheme();
    /* Theme vars on `:host` can resolve after first paint; re-read after layout. */
    requestAnimationFrame(() => {
      syncScheme();
      requestAnimationFrame(syncScheme);
    });
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener("change", onColorSchemeSignal);
    const docRoots = [document.documentElement, document.body].filter(
      Boolean,
    ) as Element[];
    const docMo = new MutationObserver(onColorSchemeSignal);
    for (const el of docRoots) {
      docMo.observe(el, {
        attributes: true,
        attributeFilter: ["data-theme", "class"],
      });
    }
    const host = $host();
    let hostMo: MutationObserver | null = null;
    if (host) {
      hostMo = new MutationObserver(onColorSchemeSignal);
      hostMo.observe(host, {
        attributes: true,
        attributeFilter: ["data-theme", "class"],
      });
    }
    return () => {
      mq.removeEventListener("change", onColorSchemeSignal);
      docMo.disconnect();
      hostMo?.disconnect();
    };
  });

  function changeSort(key: string) {
    if (!sortedBy || key !== sortedBy) {
      sortedDirection = "asc";
      sortedBy = key;
    } else if (key === sortedBy && sortedDirection === "asc") {
      sortedDirection = "desc";
    } else if (key === sortedBy && sortedDirection === "desc") {
      if (sortStrictDirectionTable) {
        sortedDirection = "asc";
      } else {
        sortedDirection = "asc";
        sortedBy = undefined;
      }
    } else if (key === sortedBy && sortedDirection === "default") {
      sortedDirection = "asc";
    }
    dispatch("changeSort", {
      sortedBy,
      sortedDirection,
    });
  }
  function dialogShowConfirm(
    detail: DialogEvents["modalShow"],
    action: string,
  ) {
    dispatch("showConfirmModal", Object.assign({ action }, detail));

    if (!detail.show)
      modalConfirm = {
        show: "no",
        itemId: "",
        action: "",
        confirmlabel: undefined,
        title: undefined,
        content: undefined,
        closelabel: undefined,
      };
  }
  function dialogShowConfirmForm(
    detail: DialogformEvents["modalShow"],
    action: string,
  ) {
    dispatch("showConfirmModalForm", Object.assign({ action }, detail));

    if (!detail.show)
      modalConfirmForm = {
        show: "no",
        itemId: "",
        action: "",
        confirmlabel: undefined,
        title: undefined,
        content: undefined,
        closelabel: undefined,
      };
  }
  function dialogConfirmation(
    detail: DialogEvents["modalConfirm"],
    action: string,
  ) {
    dispatch("confirmActionModal", Object.assign({ action }, detail));
  }
  function modalFormConfirmation(
    detail: DialogformEvents["modalFormConfirm"],
    action: string,
  ) {
    console.log("confirmActionModalForm", action, "action");
    dispatch(
      "confirmActionModalForm",
      Object.assign({}, detail, { _action: action }),
    );
  }
  function copyToClipBoard(content: string) {
    navigator.clipboard.writeText(content);
    dispatch("clipboardCopyText", { text: content });
  }

  function handleAddItem() {
    dispatch("addItem", { id });
  }

  /**
   * Deterministic value in [0, mod) from row/col/salt (stable across re-renders, no Math.random).
   */
  function skeletonPick(
    row: number,
    col: number,
    salt: number,
    mod: number,
  ): number {
    if (mod <= 0) return 0;
    const h =
      ((row + 1) * 73856093) ^
      ((col + 1) * 19349663) ^
      ((salt + 1) * 83492791) ^
      (row * col * 17);
    return (h >>> 0) % mod;
  }

  /** Line width % for Bulma `.skeleton-lines` (deterministic per cell). */
  function skeletonLineWidthPct(
    rowIdx: number,
    colIdx: number,
    line: 0 | 1,
  ): number {
    const wLow = line === 0 ? 52 : 34;
    const wSpread = line === 0 ? 38 : 30;
    return wLow + skeletonPick(rowIdx, colIdx, 23 + line, wSpread);
  }

  function skeletonCellLinesStyle(
    rowIdx: number,
    colIdx: number,
    double: boolean,
  ): string {
    const w1 = skeletonLineWidthPct(rowIdx, colIdx, 0);
    if (!double) return `--hb-sk-w1: ${w1}%;`;
    const w2 = skeletonLineWidthPct(rowIdx, colIdx, 1);
    return `--hb-sk-w1: ${w1}%; --hb-sk-w2: ${w2}%;`;
  }

  /** Actions column: skeleton button width (rem). */
  function skeletonActionWidthRem(rowIdx: number): string {
    const wRem = 3.35 + skeletonPick(rowIdx, 0, 41, 22) * 0.085;
    return `${wRem.toFixed(2)}rem`;
  }

  function skeletonUseDoubleLine(rowIdx: number, colIdx: number): boolean {
    return skeletonPick(rowIdx, colIdx, 5, 10) < 3;
  }

  /** Normalise `btnFill` from JSON / loose props (string `yes` / `no`, etc.). */
  function coercedTriStateFill(fill: unknown): boolean | undefined {
    if (fill === undefined || fill === null) return undefined;
    if (fill === true) return true;
    if (fill === false) return false;
    if (typeof fill === "string") {
      const s = fill.trim().toLowerCase();
      if (s === "") return undefined;
      if (s === "yes" || s === "true" || s === "1") return true;
      if (s === "no" || s === "false" || s === "0") return false;
    }
    if (typeof fill === "number") {
      if (fill === 1) return true;
      if (fill === 0) return false;
    }
    return undefined;
  }

  /**
   * `btnFill === true` / `"yes"` → filled (no outline). Any other value (omitted, `false`, `"no"`)
   * → outlined only while the **resolved** Bulma theme on `:host` is dark
   * (`brightnessFromBulmaHost` / `computeColorSchemeDark`). Explicit `"no"` must not force outline
   * in light mode (it only means “not filled”).
   */
  function tableActionUseOutlined(fill?: unknown): boolean {
    const f = coercedTriStateFill(fill);
    if (f === true) return false;
    return colorSchemeDark;
  }

  /** Ensures compact toolbar / row action buttons (`is-small` is the table default). */
  function tableActionButtonEnsureSmall(cls: string): string {
    const c = cls.trim();
    if (/\bis-small\b/.test(c)) return c;
    return `${c} is-small`;
  }

  /** Maps action `btnClass` (Bootstrap-style suffix) to Bulma `button` + `is-*`.
   *  When `btnClass` is omitted, uses Bulma link colour. Outline follows `tableActionUseOutlined(fill)`. */
  function tableActionButtonClass(
    btnClass: string | undefined,
    fallback: "link" | "light",
    fill?: unknown,
  ): string {
    const hasClass = Boolean(btnClass?.trim());
    const outline = tableActionUseOutlined(fill) ? " is-outlined" : "";
    if (!hasClass) {
      return tableActionButtonEnsureSmall(`button is-link${outline}`);
    }
    const raw = (btnClass ?? fallback)
      .trim()
      .toLowerCase()
      .replace(/^btn-/, "");
    const map: Record<string, string> = {
      primary: "is-primary",
      secondary: "is-light",
      success: "is-success",
      danger: "is-danger",
      warning: "is-warning",
      info: "is-info",
      light: "is-light",
      dark: "is-dark",
      link: "is-link",
    };
    const mod = map[raw] || (fallback === "light" ? "is-light" : "is-ghost");
    return tableActionButtonEnsureSmall(`button ${mod}${outline}`);
  }

  /** For attributes that sometimes stored a full `btn btn-*` string (legacy). */
  function tableActionButtonClassLoose(
    btnClass: string | undefined,
    fill?: unknown,
  ): string {
    const outline = tableActionUseOutlined(fill) ? " is-outlined" : "";
    if (!btnClass?.trim()) {
      return tableActionButtonEnsureSmall(`button is-link${outline}`);
    }
    const t = btnClass.trim().toLowerCase();
    if (t.startsWith("button ")) {
      let out = t;
      if (tableActionUseOutlined(fill) && !t.includes("is-outlined")) {
        out = `${t} is-outlined`;
      }
      return tableActionButtonEnsureSmall(out);
    }
    if (t.startsWith("btn ")) {
      const rest = t.slice(4).trim().replace(/^btn-/, "");
      return tableActionButtonClass(rest, "light", fill);
    }
    return tableActionButtonClass(t.replace(/^btn-/, ""), "light", fill);
  }
</script>

<svelte:head>
  <link
    rel="stylesheet"
    href="https://cdn.jsdelivr.net/npm/bootstrap-icons@latest/font/bootstrap-icons.css"
  />
</svelte:head>
<hb-dialogform
  {i18nlang}
  id={modalConfirmForm.itemId || "confirmationModalForm"}
  show={modalConfirmForm.show}
  title={modalConfirmForm.title}
  confirmlabel={modalConfirmForm.confirmlabel || "Conferma"}
  content={modalConfirmForm.content}
  closelabel={modalConfirmForm.closelabel || "Close"}
  schema={modalConfirmForm.schema
    ? JSON.stringify(modalConfirmForm.schema)
    : "[]"}
  onmodalFormConfirm={(e: { detail: DialogformEvents["modalFormConfirm"] }) =>
    modalFormConfirmation(e.detail, modalConfirmForm.action)}
  onmodalShow={(d: { detail: DialogformEvents["modalShow"] }) =>
    dialogShowConfirmForm(d.detail, modalConfirmForm.action)}
></hb-dialogform>
<hb-dialog
  {i18nlang}
  id={modalConfirm.itemId || "confirmationModal"}
  show={modalConfirm.show}
  title={modalConfirm.title}
  confirmlabel={modalConfirm.confirmlabel || "Conferma"}
  content={modalConfirm.content}
  closelabel={modalConfirm.closelabel || "Close"}
  onmodalConfirm={(e: { detail: DialogEvents["modalConfirm"] }) =>
    dialogConfirmation(e.detail, modalConfirm.action)}
  onmodalShow={(d: { detail: DialogEvents["modalShow"] }) =>
    dialogShowConfirm(d.detail, modalConfirm.action)}
></hb-dialog>

{#if headers && Array.isArray(headers)}
  <div class="table-container">
    <table
      part="table"
      class="table is-fullwidth is-striped is-hoverable {fixedColumnsTable
        ? 'hb-table--fixed'
        : ''}"
      aria-busy={isLoadingTable}
    >
      <thead>
        <tr>
          {#if enableselect && selectactions?.length}
            {#if !searchOnRangeIsPresent}
              <th scope="col">
                {#if selectedItems.length !== rowsTable.length}
                  <button
                    aria-label="Select all"
                    onclick={selectAllElements}
                    class="button is-ghost is-small"
                    >{translator.translateWord("selectAll")}</button
                  >
                {:else}
                  <button
                    aria-label="Deselect all"
                    onclick={deSelectAllElements}
                    class="button is-ghost is-small"
                    >{translator.translateWord("deselectAll")}</button
                  >
                {/if}
              </th>
            {:else}
              <th scope="col"></th>
            {/if}
          {/if}
          {#each headers as th (th.key)}
            <th scope="col" class={headerCenterHeaderClass(th)}>
              <div class="hb-table-th-title-row">
                {#if !th.nosort && th.type !== "actions"}
                  <button
                    type="button"
                    class="button is-ghost is-small hb-table-sort-btn"
                    onclick={() => changeSort(th.key)}
                    aria-label="Sort by {th.label}"
                  >
                    {#if !sortedBy || th.key !== sortedBy}
                      <i
                        class="bi bi-arrow-down-up hb-table-sort-ico"
                        aria-hidden="true"
                      ></i>
                    {:else if sortedDirection === "asc"}
                      <i
                        class="bi bi-sort-up hb-table-sort-ico"
                        aria-hidden="true"
                      ></i>
                    {:else if sortedDirection === "desc"}
                      <i
                        class="bi bi-sort-down hb-table-sort-ico"
                        aria-hidden="true"
                      ></i>
                    {/if}
                  </button>
                {/if}
                <span
                  class="hb-table-th-label"
                  class:hb-table-th-label--center-header={th.centerHeader ===
                    true}
                >
                  {#if th.tooltip}
                    <hb-tooltip tooltip={JSON.stringify(th.tooltip)}>
                      {th.label}
                      <i
                        class="bi bi-info-circle"
                        aria-hidden="true"
                      ></i>
                    </hb-tooltip>
                  {:else}
                    {th.label}
                  {/if}
                </span>
              </div>
            </th>
          {/each}
          {#if actions}
            <th
              scope="col"
              style="min-width: 8em;width: calc(4em * {actions.length})"
            >
              {translator.translateWord("actions")}
            </th>
          {/if}
        </tr>
        {#if !searchOnRangeIsPresent}
          <tr>
            {#if enableselect && selectactions?.length}
              <th scope="col">
                {selectedItems.length}/{rowsTable.length}
              </th>
            {/if}
            {#if headers.find((f) => f.search)}
              {#each headers as th (th.key)}
                <th scope="col" class={headerCenterHeaderClass(th)}>
                  {#if th.search}
                    {#if th.type && th.type === "datetime"}
                      <span class="hb-table-header-date-filter">
                        <span class="hb-table-header-date-filter__label"
                          >{translator.translateWord("start")}</span
                        >
                        <input
                          oninput={(element) =>
                            changeStartDate(element.target, th)}
                          type="date"
                          class="input"
                        />
                      </span>
                    {:else if !th.type || th.type === "string" || th.type === "ip"}
                      <input
                        oninput={(element) => searchInput(element.target, th)}
                        type="text"
                        class="input"
                        placeholder="..."
                        aria-label="Search"
                        aria-describedby="search"
                      />
                    {:else if th.type && th.type === "enum" && th.select?.length}
                      <div class="select is-fullwidth">
                        <select
                          oninput={(element) => searchInput(element.target, th)}
                        >
                          <option value=""
                            >{translator.translateWord("all")}</option
                          >

                          {#each th.select as se (se)}
                            <option value={se}>{se}</option>
                          {/each}
                        </select>
                      </div>
                    {/if}
                  {/if}
                  {#if !th.search}
                    &nbsp;
                  {/if}
                </th>
              {/each}
              {#if actions}
                <th scope="col">&nbsp;</th>
              {/if}
            {/if}
          </tr>
        {:else}
          <tr>
            {#if enableselect && selectactions?.length}
              <th scope="col">
                {#if selectedItems.length !== rowsTable.length}
                  <button
                    onclick={selectAllElements}
                    class="button is-ghost is-small"
                    >{translator.translateWord("selectAll")}</button
                  >
                {:else}
                  <button
                    onclick={deSelectAllElements}
                    class="button is-ghost is-small"
                    >{translator.translateWord("deselectAll")}</button
                  >
                {/if}
              </th>
            {/if}
            {#each headers as th (th.key)}
              <th scope="col" class={headerCenterHeaderClass(th)}>
                {#if th.search}
                  {#if th.type && th.type === "datetime"}
                    <span class="hb-table-header-date-filter">
                      <span class="hb-table-header-date-filter__label"
                        >Inizio</span
                      >
                      <input
                        oninput={(element) =>
                          changeStartDate(element.target, th)}
                        type="date"
                        class="input"
                      />
                    </span>
                  {:else if !th.type || th.type === "string" || th.type === "ip"}
                    <input
                      oninput={(element) => searchInput(element.target, th)}
                      type="text"
                      class="input"
                      placeholder="..."
                      aria-label="Search"
                      aria-describedby="search"
                    />
                  {:else if th.type && th.type === "enum" && th.select?.length}
                    <div class="select is-fullwidth">
                      <select
                        oninput={(element) => searchInput(element.target, th)}
                      >
                        <option value=""
                          >{translator.translateWord("all")}</option
                        >

                        {#each th.select as se (se)}
                          <option value={se}>{se}</option>
                        {/each}
                      </select>
                    </div>
                  {/if}
                {:else}
                  &nbsp;
                {/if}
              </th>
            {/each}
            {#if actions}
              <th scope="col">&nbsp;</th>
            {/if}
          </tr>
          <tr>
            {#if enableselect && selectactions?.length}
              <th scope="col">
                {selectedItems.length}/{rowsTable.length}
              </th>
            {/if}
            {#each headers as th (th.key)}
              <th scope="col" class={headerCenterHeaderClass(th)}>
                {#if th.search && th.type && th.type === "datetime"}
                  <span class="hb-table-header-date-filter">
                    <span class="hb-table-header-date-filter__label"
                      >{translator.translateWord("end")}</span
                    >
                    <input
                      oninput={(element) => changeEndDate(element.target, th)}
                      type="date"
                      class="input"
                    />
                  </span>
                {/if}
              </th>
            {/each}
            {#if actions}
              <th scope="col">&nbsp;</th>
            {/if}
          </tr>
        {/if}
      </thead>
      <tbody>
        {#if isLoadingTable}
          {#each Array(loadingSkeletonRowCount) as _, rowIdx (rowIdx)}
            <tr class="hb-skeleton-row">
              {#if enableselect && selectactions?.length}
                <td>
                  <label
                    class="checkbox is-skeleton hb-table-sk-cb"
                    aria-hidden="true"
                  >
                    <input type="checkbox" disabled tabindex="-1" />
                  </label>
                </td>
              {/if}
              {#each headers as h, colIdx (h.key)}
                <td class={headerCenterCellClass(h)}>
                  <div
                    class="skeleton-lines hb-table-sk-lines"
                    style={skeletonCellLinesStyle(
                      rowIdx,
                      colIdx,
                      skeletonUseDoubleLine(rowIdx, colIdx),
                    )}
                    aria-hidden="true"
                  >
                    <div></div>
                    {#if skeletonUseDoubleLine(rowIdx, colIdx)}
                      <div></div>
                    {/if}
                  </div>
                </td>
              {/each}
              {#if actions}
                <td>
                  <button
                    type="button"
                    class="button is-small is-skeleton hb-table-sk-action"
                    style={`width: ${skeletonActionWidthRem(rowIdx)};`}
                    aria-hidden="true"
                    tabindex="-1"
                    disabled
                  ></button>
                </td>
              {/if}
            </tr>
          {/each}
        {:else if rowsTable?.length}
          {#each !externalfilterTable ? rowsTable.slice(Number(page) * Number(size), (Number(page) + 1) * Number(size)) : rowsTable as item (item._id)}
            <tr
              class={[
                selected && selected === item._id ? "is-selected" : "",
                item._evidenced
                  ? "has-background-info-soft hb-table-row-evidenced"
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
              part={selected && selected === item._id
                ? "selected-row"
                : "common-row"}
            >
              {#if enableselect && selectactions?.length}
                <td class="hb-table-checkbox-cell">
                  <label class="checkbox">
                    {#if selectedItems.find((f) => f === item._id)}
                      <input
                        onchange={(i) => {
                          handleSelectedItem(item._id, i.target);
                        }}
                        type="checkbox"
                        checked
                      />
                    {:else}
                      <input
                        onchange={(i) => {
                          handleSelectedItem(item._id, i.target);
                        }}
                        type="checkbox"
                      />
                    {/if}
                  </label>
                </td>
              {/if}
              {#if headers.length}
                {#each headers as td (td.key)}
                  {#if td.type === "actions"}
                    {#if item._actions?.length && typeof item._actions === "object"}
                      <td
                        class={[
                          "hb-table-actions-cell",
                          headerCenterCellClass(td),
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        <div class="hb-table-actions-inner">
                          {#each item._actions as abutton (abutton.name)}
                            {#if abutton.disabled}
                              {#if abutton.type === "text"}
                                {#if abutton.tooltip}
                                  <hb-tooltip
                                    tooltip={JSON.stringify(abutton.tooltip)}
                                  >
                                    <button
                                      onclick={() =>
                                        handleClickOnCustomAction(
                                          item,
                                          abutton,
                                        )}
                                      type="button"
                                      class={tableActionButtonClass(
                                        abutton.btnClass,
                                        "link",
                                        abutton.btnFill,
                                      )}
                                      disabled>{abutton.iconOrText}</button
                                    >
                                  </hb-tooltip>
                                {:else}
                                  <button
                                    onclick={() =>
                                      handleClickOnCustomAction(item, abutton)}
                                    type="button"
                                    class={tableActionButtonClass(
                                      abutton.btnClass,
                                      "link",
                                      abutton.btnFill,
                                    )}
                                    disabled>{abutton.iconOrText}</button
                                  >
                                {/if}
                              {:else if abutton.type === "icon"}
                                {#if abutton.tooltip}
                                  <hb-tooltip
                                    tooltip={JSON.stringify(abutton.tooltip)}
                                  >
                                    <button
                                      onclick={() =>
                                        handleClickOnCustomAction(
                                          item,
                                          abutton,
                                        )}
                                      type="button"
                                      class={abutton.btnClass
                                        ? tableActionButtonClassLoose(
                                            abutton.btnClass,
                                            abutton.btnFill,
                                          )
                                        : tableActionButtonClass(
                                            undefined,
                                            "light",
                                            abutton.btnFill,
                                          )}
                                      disabled
                                      aria-label={abutton.iconOrText}
                                      ><i class="bi-{abutton.iconOrText}"></i>
                                    </button>
                                  </hb-tooltip>
                                {:else}
                                  <button
                                    onclick={() =>
                                      handleClickOnCustomAction(item, abutton)}
                                    type="button"
                                    class={tableActionButtonClass(
                                      abutton.btnClass,
                                      "light",
                                      abutton.btnFill,
                                    )}
                                    disabled
                                    aria-label={abutton.iconOrText}
                                    ><i class="bi-{abutton.iconOrText}"></i>
                                  </button>
                                {/if}
                              {/if}
                            {:else if abutton.type === "text"}
                              <button
                                onclick={() =>
                                  handleClickOnCustomAction(item, abutton)}
                                type="button"
                                class={tableActionButtonClass(
                                  abutton.btnClass,
                                  "link",
                                  abutton.btnFill,
                                )}>{abutton.iconOrText}</button
                              >
                            {:else if abutton.type === "icon"}
                              {#if abutton.tooltip}
                                <hb-tooltip
                                  tooltip={JSON.stringify(abutton.tooltip)}
                                >
                                  <button
                                    type="button"
                                    class={abutton.btnClass
                                      ? tableActionButtonClassLoose(
                                          abutton.btnClass,
                                          abutton.btnFill,
                                        )
                                      : tableActionButtonClass(
                                          undefined,
                                          "light",
                                          abutton.btnFill,
                                        )}
                                    onclick={() =>
                                      handleClickOnCustomAction(item, abutton)}
                                    aria-label={abutton.iconOrText}
                                  >
                                    <i class="bi-{abutton.iconOrText}"></i>
                                  </button>
                                </hb-tooltip>
                              {:else}
                                <button
                                  onclick={() =>
                                    handleClickOnCustomAction(item, abutton)}
                                  type="button"
                                  class={tableActionButtonClass(
                                    abutton.btnClass,
                                    "light",
                                    abutton.btnFill,
                                  )}
                                  aria-label={abutton.iconOrText}
                                  ><i class="bi-{abutton.iconOrText}"></i>
                                </button>
                              {/if}
                            {/if}
                          {/each}
                        </div>
                      </td>
                    {:else}
                      <td class={headerCenterCellClass(td)}></td>
                    {/if}
                  {:else}
                    <td
                      class={headerCenterCellClass(td)}
                      onclick={() => {
                        if (selectrow) clickonrow(item._id);
                      }}
                    >
                      {#if td.click}
                        <button
                          onclick={() => handleClickOnRow(item._id, td.key)}
                          type="button"
                          class="button is-ghost is-small"
                          >{getObjVal(item, td, true)}</button
                        >
                      {:else}
                        {getObjVal(item, td, true)}
                      {/if}
                      {#if (td.type === "string" || td.type === "ip" || !td.type) && getObjVal(item, td).toString().length && td.copyTxt}
                        <button
                          onclick={() =>
                            copyToClipBoard(getObjVal(item, td).toString())}
                          type="button"
                          class="button is-ghost is-small"
                          aria-label="Copy to clipboard"
                          ><i class="bi-clipboard"></i></button
                        >
                      {/if}
                    </td>
                  {/if}
                {/each}
              {/if}
              {#if actions && typeof actions === "object"}
                <td class="hb-table-actions-cell">
                  <div class="hb-table-actions-inner">
                    {#each actions as abutton (abutton.name)}
                      {#if abutton.type === "text"}
                        <button
                          onclick={() =>
                            handleClickOnAction(item._id, abutton.name)}
                          type="button"
                          class={tableActionButtonClass(
                            abutton.btnClass,
                            "link",
                            abutton.btnFill,
                          )}>{abutton.iconOrText}</button
                        >
                      {:else if abutton.type === "icon"}
                        {#if abutton.tooltip}
                          <hb-tooltip tooltip={JSON.stringify(abutton.tooltip)}>
                            <button
                              onclick={() =>
                                handleClickOnAction(item._id, abutton.name)}
                              type="button"
                              class={tableActionButtonClass(
                                abutton.btnClass,
                                "light",
                                abutton.btnFill,
                              )}
                              aria-label={abutton.iconOrText}
                              ><i class="bi-{abutton.iconOrText}"></i>
                            </button>
                          </hb-tooltip>
                        {:else}
                          <button
                            onclick={() =>
                              handleClickOnAction(item._id, abutton.name)}
                            type="button"
                            class={tableActionButtonClass(
                              abutton.btnClass,
                              "light",
                              abutton.btnFill,
                            )}
                            aria-label={abutton.iconOrText}
                            ><i class="bi-{abutton.iconOrText}"></i>
                          </button>
                        {/if}
                      {/if}
                    {/each}
                  </div>
                </td>
              {/if}
            </tr>
          {/each}
        {/if}
      </tbody>
    </table>
  </div>
  <div
    part="table-actions"
    class="hb-table-toolbar"
    role="navigation"
    aria-label="actions on selected"
  >
    {#if selectactions?.length}
      <nav
        class="pagination is-small hb-table-toolbar-pagination"
        aria-label={translator.translateWord("actions")}
      >
        <ul class="pagination-list">
          <li>
            <button
              type="button"
              class="pagination-link"
              class:is-current={enableselect}
              onclick={handleEnableSelector}
              aria-label="Settings"
              aria-pressed={enableselect ? "true" : "false"}
            >
              <i class="bi-gear" aria-hidden="true"></i>
            </button>
          </li>
          {#if !enableselect && add_item}
            <li>
              <button
                onclick={handleAddItem}
                type="button"
                class="pagination-link is-current"
                aria-label={translator.translateWord("add")}
              >
                <slot name="add-button-content"
                  ><i class="bi-plus" aria-hidden="true"></i></slot
                >
              </button>
            </li>
          {/if}
          {#if enableselect}
            {#each selectactions as sbutton (sbutton.name)}
              <li>
                <button
                  type="button"
                  class="pagination-link"
                  onclick={() => {
                    handleClickOnMultipleSelectAction(sbutton.name);
                  }}
                >
                  {sbutton.name}
                </button>
              </li>
            {/each}
          {/if}
        </ul>
      </nav>
    {:else if !enableselect && add_item}
      <nav
        class="pagination is-small hb-table-toolbar-pagination"
        aria-label={translator.translateWord("add")}
      >
        <ul class="pagination-list">
          <li>
            <button
              onclick={handleAddItem}
              type="button"
              class="pagination-link is-current"
              aria-label={translator.translateWord("add")}
            >
              <slot name="add-button-content"
                ><i class="bi-plus" aria-hidden="true"></i></slot
              >
            </button>
          </li>
        </ul>
      </nav>
    {/if}
    <slot name="buttons-container" />
    {#if disablepaginationTable !== true}
      <div
        class="hb-table-paginate-wrap"
        class:hb-table-paginate-wrap--loading={isLoadingTable}
      >
        <hb-paginate
          aria-hidden={isLoadingTable ? "true" : undefined}
          class="hb-table-paginate-embed"
          {i18nlang}
          info={JSON.stringify(paginatedInfo)}
          onpageChange={changePage}
          onchangePageSize={changePageSize}
          onchangeSort={changeSortFromPaginate}
          page={paginatePageAttr}
          pages={paginatePagesAttr}
        ></hb-paginate>
        {#if isLoadingTable}
          <div
            class="hb-table-paginate-skeleton--overlay"
            aria-busy="true"
            aria-label="Pagination loading"
          >
            <div
              class="skeleton-block hb-table-sk-pagination-block"
              aria-hidden="true"
            ></div>
          </div>
        {/if}
      </div>
    {/if}
  </div>
{/if}

<style lang="scss">
  @forward "./styles/bulma.scss";
  @forward "./styles/webcomponent.scss";
</style>
